/**
 * Privacy-Safe Device Fingerprinting Utility
 * 
 * Generates a device fingerprint using only publicly available data
 * that doesn't require privacy consent under most privacy laws.
 */

class DeviceFingerprint {
    constructor() {
        this.fingerprint = null;
        this.lastGenerated = null;
        this.cacheExpiry = 5 * 60 * 1000; // 5 minutes
    }

    /**
     * Generate privacy-safe device fingerprint
     * @returns {string} Device fingerprint hash
     */
    generateFingerprint() {
        // Check cache first
        if (this.fingerprint && this.lastGenerated && 
            (Date.now() - this.lastGenerated) < this.cacheExpiry) {
            return this.fingerprint;
        }

        const fingerprintData = {
            // HTTP headers data (automatically sent)
            userAgent: navigator.userAgent,
            language: navigator.language,
            languages: navigator.languages?.join(',') || '',
            platform: navigator.platform,
            
            // Basic capabilities (safe)
            cookieEnabled: navigator.cookieEnabled,
            hardwareConcurrency: navigator.hardwareConcurrency || 0,
            
            // Screen info (basic, safe)
            screenResolution: `${screen.width}x${screen.height}`,
            colorDepth: screen.colorDepth,
            
            // Timezone (functional data)
            timezoneOffset: new Date().getTimezoneOffset(),
            
            // Math operations (device-specific)
            mathFingerprint: [
                Math.tan(-1e300),
                Math.sin(1e300),
                Math.cos(1e300)
            ].join(','),
            
            // Browser features (safe)
            features: [
                typeof(Storage) !== "undefined" ? 'localStorage' : '',
                typeof(indexedDB) !== "undefined" ? 'indexedDB' : '',
                this.supportsCSS('display', 'grid') ? 'grid' : '',
                this.supportsCSS('display', 'flex') ? 'flex' : ''
            ].filter(Boolean).join(',')
        };
        
        // Create hash of all safe data
        const fingerprintString = JSON.stringify(fingerprintData);
        this.fingerprint = this.simpleHash(fingerprintString);
        this.lastGenerated = Date.now();
        
        return this.fingerprint;
    }

    /**
     * Check CSS support
     * @param {string} property - CSS property
     * @param {string} value - CSS value
     * @returns {boolean} Support status
     */
    supportsCSS(property, value) {
        try {
            return CSS && CSS.supports && CSS.supports(property, value);
        } catch (e) {
            return false;
        }
    }

    /**
     * Simple hash function for fingerprinting
     * @param {string} str - String to hash
     * @returns {string} Hash value
     */
    simpleHash(str) {
        let hash = 0;
        if (str.length === 0) return hash.toString();
        
        for (let i = 0; i < str.length; i++) {
            const char = str.charCodeAt(i);
            hash = ((hash << 5) - hash) + char;
            hash = hash & hash; // Convert to 32-bit integer
        }
        
        return Math.abs(hash).toString(36).substring(0, 16);
    }

    /**
     * Get current device fingerprint
     * @returns {string} Current fingerprint
     */
    getCurrentFingerprint() {
        return this.generateFingerprint();
    }

    /**
     * Clear cached fingerprint (force regeneration)
     */
    clearCache() {
        this.fingerprint = null;
        this.lastGenerated = null;
    }
}

// Create singleton instance
const deviceFingerprint = new DeviceFingerprint();

export default deviceFingerprint;
